// src/pages/BuyerDashboard.js
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Container, Box, Typography, Button, Slider, Stack,
  Grid, Tabs, Tab, IconButton, useTheme, useMediaQuery, Dialog, CircularProgress, Tooltip
 } from '@mui/material';
import BuyerProfileCard from '../components/BuyerProfileCard';
import LeafletMapNearbyUsers from '../components/LeafletMapNearbyUsers';
import ChatModal from '../components/ChatModal';
import subcategorySchema from '../utils/subcategorySchema';
import { http } from "../lib/axios";
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { FavoritesSection } from "../components/FavoritesSection";
import HostProfileCard from '../components/HostProfileCard';
import ArtistProfileCard from '../components/ArtistProfileCard';
import { api } from "../lib/api";
import { requestPaymentRefund } from "../lib/apiPayments";
import { publicGet } from "../lib/apiPublic";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MiniArtworkCard from "../components/MiniArtworkCard";
import ArtworkCard from "../components/ArtworkCard";
import ArtworkSearchFiltersModal from "../components/ArtworkSearchFiltersModal";

const blurredTextSx = {
  display: 'inline-block',
  backgroundColor: 'rgba(255, 255, 255, 0.6)',
  backdropFilter: 'blur(14px)',
  borderRadius: 1.5,
  px: 1.5,
  py: 0.75,
  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
  color: '#111827'
};

function normalizeArtworkForCard(item) {
  const artwork = item?.artwork && typeof item.artwork === 'object' ? item.artwork : null;
  if (!artwork) return item || {};

  return {
    ...item,
    ...artwork,
    id: artwork.id ?? item.artwork_id ?? item.id,
    status: artwork.status ?? item.status,
    host: artwork.host ?? item.host,
    host_id: artwork.host_id ?? item.host_id,
    host_name: artwork.host_name ?? item.host_name,
    host_display_name: artwork.host_display_name ?? item.host_display_name,
    subcategories: artwork.subcategories ?? item.subcategories,
    dimensions: artwork.dimensions ?? item.dimensions,
    engagement_summary: artwork.engagement_summary ?? item.engagement_summary,
  };
}

export default function BuyerDashboard() {
  const [distance, setDistance] = useState(10);
  const [buyerProfile, setBuyerProfile] = useState(null);
  //const [nearbyArtworks, setNearbyArtworks] = useState([]);
  const [recipient, setRecipient] = useState(null);

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState({});
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [chatOpen, setChatOpen] = useState(false);
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [authUserId, setAuthUserId] = useState(null);
  const [nearbyArtistArtworks, setNearbyArtistArtworks] = useState([]);
  const [nearbyHostedArtworks, setNearbyHostedArtworks] = useState([]);
  const messagesEndRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [nearbySubTab, setNearbySubTab] = useState(0); // 0 hosts, 1 artists
  const [categoryFilteredArtworks, setCategoryFilteredArtworks] = useState([]);
 
  const ARTWORK_STATUSES = ["available","hosted","approved","sold","featured"];
  const [statusFilterNearby, setStatusFilterNearby] = useState([]); // [] => show all
  const [nearbyHosts, setNearbyHosts] = useState([]);
  const [nearbyArtists, setNearbyArtists] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchasesError, setPurchasesError] = useState("");
  const [purchasesLoaded, setPurchasesLoaded] = useState(false);
  const [purchaseActionMessage, setPurchaseActionMessage] = useState("");
  const [refundingSaleId, setRefundingSaleId] = useState(null);
  const purchasesTrackRef = useRef(null);
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const PURCHASE_CARD_WIDTH = 260;
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const [selectedPurchaseArtwork, setSelectedPurchaseArtwork] = useState(null);
  const [selectedPurchaseLoading, setSelectedPurchaseLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const getFullProfile = (profile) => {
    const fullProfile =  {
      ...profile,
      first_name: profile.user?.first_name,
      last_name: profile.user?.last_name,
      email: profile.user?.email,
    };  
    return(fullProfile);
  }

  const filteredNearbyHosts = useMemo(() => (
    (Array.isArray(nearbyHosts) ? nearbyHosts : []).filter((host) => {
      const role = host?.role || host?.user?.role;
      const userId = host?.user?.id || host?.user_id;
      if (authUserId && Number(userId) === Number(authUserId)) return false;
      return !role || role === 'host';
    })
  ), [nearbyHosts, authUserId]);

  const filteredNearbyArtists = useMemo(() => (
    (Array.isArray(nearbyArtists) ? nearbyArtists : []).filter((artist) => {
      const role = artist?.role || artist?.user?.role;
      const userId = artist?.user?.id || artist?.user_id;
      if (authUserId && Number(userId) === Number(authUserId)) return false;
      return !role || role === 'artist';
    })
  ), [nearbyArtists, authUserId]);


  useEffect(() => {
    const fetchBuyerData = async () => {
      try {
        const {data} = await http.get('/api/accounts/me/');
        const userId = data.id;
        setAuthUserId(userId);

        const buyerRes = await http.get(`/buyers/?user=${userId}`);
        if (!buyerRes.data.length) {
          alert('You need to complete your buyer profile before accessing the dashboard.');
          navigate('/buyer-profile/setup');
          return;
        }

        const fullProfile = {
          ...buyerRes.data[0],
          first_name: buyerRes.data[0].user.first_name,
          last_name: buyerRes.data[0].user.last_name,
          email: buyerRes.data[0].user.email,
        };

        // nearby hosts
        try {
          const nh = await http.get(`/hosts/nearby/?lat=${fullProfile.location.latitude}&lon=${fullProfile.location.longitude}&radius=${distance}&view=card`);
          setNearbyHosts(nh.data || []);
        } catch {
          setNearbyHosts([]);
        }

        // nearby artists
        try {
          const na = await http.get(`/artists/nearby/?lat=${fullProfile.location.latitude}&lon=${fullProfile.location.longitude}&radius=${distance}&view=card`);
          setNearbyArtists(na.data || []);
        } catch {
          setNearbyArtists([]);
        }

        setBuyerProfile(fullProfile);
        localStorage.setItem('buyer_id', fullProfile.id);
        console.log('Buyer Profile:', fullProfile);
      } catch (error) {
        console.error('Failed to load buyer dashboard:', error);
      }
    };

    fetchBuyerData();
  }, [distance, navigate]);

  const loadPurchases = React.useCallback(async ({ force = false } = {}) => {
    if (purchasesLoaded && !force) return;
    setPurchasesLoading(true);
    setPurchasesError("");
    try {
      const data = await api.getJson("/buyers/purchases/me?limit=50");
      setPurchases(data.results || []);
      setPurchasesLoaded(true);
    } catch (e) {
      setPurchasesError(e.message || "Failed to load purchases.");
    } finally {
      setPurchasesLoading(false);
    }
  }, [purchasesLoaded]);

  useEffect(() => {
    if (tab === 2 && !purchasesLoaded) {
      loadPurchases();
    }
  }, [tab, purchasesLoaded, loadPurchases]);

  const handleRequestRefund = async (purchase) => {
    const paymentId = purchase?.payment?.id;
    if (!paymentId) return;
    const approverLabel = purchase?.payment?.refund_approver_role === "host" ? "host" : "artist";
    const refundReason = window.prompt(`Why are you requesting a refund for ${purchase?.artwork?.title || "this artwork"}?`);
    if (refundReason === null) return;
    if (!refundReason.trim()) {
      setPurchaseActionMessage("A refund reason is required.");
      return;
    }

    const confirmed = window.confirm(
      `Request a refund for ${purchase?.artwork?.title || "this artwork"}? The ${approverLabel} must approve it before Stripe processes the refund.`
    );
    if (!confirmed) return;

    setPurchaseActionMessage("");
    setRefundingSaleId(purchase.sale_id);
    try {
      const result = await requestPaymentRefund(paymentId, refundReason);
      await loadPurchases({ force: true });
      setPurchaseActionMessage(result?.detail || "Refund request submitted successfully.");
    } catch (e) {
      setPurchaseActionMessage(e.message || "Unable to request a refund right now.");
    } finally {
      setRefundingSaleId(null);
    }
  };

  const scrollPurchases = (dir) => {
    const el = purchasesTrackRef.current;
    if (!el) return;
    const page = Math.max(PURCHASE_CARD_WIDTH + 16, el.clientWidth * 0.85);
    el.scrollBy({ left: dir * page, behavior: "smooth" });
  };

  const openPurchaseModal = (artId, art) => {
    if (!artId) return;
    setSelectedPurchaseArtwork(art || null);
    setSelectedPurchaseId(artId);
  };

  const closePurchaseModal = () => {
    setSelectedPurchaseId(null);
    setSelectedPurchaseArtwork(null);
    setSelectedPurchaseLoading(false);
  };

  useEffect(() => {
    if (!selectedPurchaseId) return;
    let cancel = false;
    setSelectedPurchaseLoading(true);
    (async () => {
      try {
        const { data } = await publicGet(`/artworks/${selectedPurchaseId}/`, { params: { view: "card" } });
        if (!cancel) setSelectedPurchaseArtwork(data);
      } catch (e) {
        if (!cancel) console.warn("Failed to fetch purchase artwork detail", e);
      } finally {
        if (!cancel) setSelectedPurchaseLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [selectedPurchaseId]);

  const handleStartChat = (user) => {
    setRecipient(user);
    const recipientId = user.user_id;
    console.log('Recipient ID: ', recipientId);
    http.post('/messages/start/', { recipient_id: recipientId })
      .then(res => {
        setConversation(res.data);
        console.log('conversation', res.data);
        http.get(`/messages/${res.data.id}/messages/`)
          .then(msgRes => {
            setMessages(msgRes.data);
            setChatOpen(true);
          });
      })
      .catch(err => console.error('Messaging error:', err));
  };

  const handleSend = () => {
    if (!newMsg.trim()) return;

    http.post(`/messages/${conversation.id}/send/`, {
      content: newMsg,
    }).then(res => {
      setMessages(prev => [...prev, res.data]);
      setNewMsg('');
    }).catch(err => console.error('Send message failed:', err));
  };

  // merged artworks (nearby)
  const nearbyAllArtworks = useMemo(() => {
    const a = Array.isArray(nearbyArtistArtworks)
      ? nearbyArtistArtworks.map(normalizeArtworkForCard)
      : [];
    const h = Array.isArray(nearbyHostedArtworks)
      ? nearbyHostedArtworks.map(normalizeArtworkForCard)
      : [];
    return [...a, ...h];
  }, [nearbyArtistArtworks, nearbyHostedArtworks]);

  const filteredNearbyAllArtworks = React.useMemo(() => {
    if (!Array.isArray(nearbyAllArtworks)) return [];
    if (statusFilterNearby.length === 0) return nearbyAllArtworks; // filter OFF
    const set = new Set(statusFilterNearby);
    return nearbyAllArtworks.filter(a => set.has(a?.status));
  }, [nearbyAllArtworks, statusFilterNearby]);


  useEffect(() => {
   const allArtworks = filteredNearbyAllArtworks;
   if (allArtworks) {
     const filtered = allArtworks.filter(art => {
       if (selectedCategory && art.artwork_type !== selectedCategory) return false;
       const numericPrice = Number(art?.price);
       if (minPrice !== '' && (!Number.isFinite(numericPrice) || numericPrice < Number(minPrice))) return false;
       if (maxPrice !== '' && (!Number.isFinite(numericPrice) || numericPrice > Number(maxPrice))) return false;
       if (selectedCategory && subcategorySchema[selectedCategory]) {
         for (let subKey of Object.keys(subcategorySchema[selectedCategory])) {
           if (selectedSubcategories[subKey] && art.subcategories?.[subKey] !== selectedSubcategories[subKey]) {
             return false;
            }
          }
        }
       return true;
     });
     setCategoryFilteredArtworks(filtered);
   }
  }, [filteredNearbyAllArtworks, selectedCategory, selectedSubcategories, minPrice, maxPrice]);

  return (
    <Container sx={{ mt: 5 }}>
      {/* Dashboard title */}
      <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
        <Typography variant="h4" gutterBottom align="center" sx={blurredTextSx}>
          {buyerProfile?.first_name} {buyerProfile?.last_name}'s Dashboard
        </Typography>
      </Box>

      {/* Tabs under title */}
      <Box
        sx={{
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(14px)',
          px: 1.5,
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Tooltip title="Help for buyer dashboard">
          <IconButton
            component={RouterLink}
            to="/help"
            state={{ backgroundLocation: location, helpSection: 'buyer-help' }}
            size="small"
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
              color: '#111827',
              fontWeight: 800,
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
            }}
          >
            ?
          </IconButton>
        </Tooltip>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTabs-flexContainer': { gap: 1 },
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 44,
              fontWeight: 600,
              borderRadius: 1.5,
              border: '1px solid transparent',
            },
            '& .Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.4)',
            }
          }}
        >
          <Tab label="My Profile" />
          <Tab label="Nearby Art" />
          <Tab label="My Purchases" />
          <Tab label="My Favorites" />
        </Tabs>
      </Box>

      {/* My Profile */}
      {tab === 0 && (
        <Box sx={{ mt: 2 }}>
          {buyerProfile && <BuyerProfileCard user={buyerProfile} isSelf={true} />}
        </Box>
      )}

      {/* Nearby Art */}
      {tab === 1 && (
        <>
          <Box width="100%" maxWidth="600px" mx="auto" my={2}>
            <Typography gutterBottom sx={blurredTextSx}>Search radius: {distance} km</Typography>
            <Slider
              value={distance}
              min={1}
              max={50}
              step={1}
              valueLabelDisplay="auto"
              onChange={(_e, val) => setDistance(val)}
              sx={{ mt: 1 }}
            />
          </Box>
          {buyerProfile?.location && (
            <LeafletMapNearbyUsers
              roleFilter={['artist', 'host']}
              distance={distance}
              buyer={[buyerProfile]}
              onStartChat={handleStartChat}
              setNearbyArtistArtworks={setNearbyArtistArtworks}
              setNearbyHostedArtworks={setNearbyHostedArtworks}
            />
          )}

          {/* Sub-tabs: Nearby Artworks / NearbyHosts / Nearby Artists */}
          <Box
            sx={{
              borderRadius: 1.5,
              backgroundColor: 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(14px)',
              px: 1,
              py: 0.5,
              mb: 1
            }}
          >
            <Tabs
              value={nearbySubTab}
              onChange={(e, v) => setNearbySubTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600, borderRadius: 1.5, border: '1px solid transparent' },
                '& .Mui-selected': { backgroundColor: 'rgba(255, 255, 255, 0.4)' }
              }}
            >
              <Tab label="Nearby Artworks" />
              <Tab label="Nearby Hosts" />
              <Tab label="Nearby Artists" />
            </Tabs>
          </Box>

          {/* Lists */}

          {/* Nearby Artworks (merged artist+hosted lists) */}
          {nearbySubTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setFilterModalOpen(true)}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderColor: 'rgba(255, 255, 255, 0.6)',
                    color: '#111827',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.6)',
                      borderColor: 'rgba(255, 255, 255, 0.75)',
                    },
                  }}
                >
                  Search Filters
                </Button>
                {categoryFilteredArtworks.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1, ...blurredTextSx }}>
                    ({categoryFilteredArtworks.length} shown)
                  </Typography>
                )}
              </Box>

              <Grid container spacing={3} sx={{ mt: 0 }}>
                {categoryFilteredArtworks.map((art) => (
                  <Grid item xs={12} md={6} lg={4} key={art.id}>
                    <ArtworkCard art={art} showActions={true} />
                  </Grid>
                ))}
              </Grid>

              {categoryFilteredArtworks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ...blurredTextSx }}>
                  No artworks match the selected status{statusFilterNearby.length > 1 ? "es" : ""}.
                </Typography>
              )}
            </Box>
          )}

          {/* Nearby Hosts List */}
          {nearbySubTab === 1 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {filteredNearbyHosts.map((h) => (
                  <Grid item xs={12} md={6} lg={4} key={h.id}>
                    <HostProfileCard user={getFullProfile(h)} onStartChat={handleStartChat} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Nearby Artists List */}
          {nearbySubTab === 2 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                {filteredNearbyArtists.map((a) => (
                  <Grid item xs={12} md={6} lg={4} key={a.id}>
                    <ArtistProfileCard user={getFullProfile(a)} onStartChat={handleStartChat} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

        </>

      )} {/* End of Nearby Art Tab */}

      {/* My Purchases Tab */}
      {tab === 2 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(14px)',
            boxShadow: '0 8px 18px rgba(0,0,0,0.08)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Tooltip title="Help for purchases and refunds">
              <IconButton
                component={RouterLink}
                to="/help"
                state={{ backgroundLocation: location, helpSection: 'payments-help' }}
                size="small"
                sx={{
                  width: 34,
                  height: 34,
                  backgroundColor: 'rgba(255, 255, 255, 0.75)',
                  backdropFilter: 'blur(14px)',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  color: '#111827',
                  fontWeight: 800,
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.95)' },
                }}
              >
                ?
              </IconButton>
            </Tooltip>
          </Box>

          {purchasesLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ...blurredTextSx }}>
              Loading purchases…
            </Typography>
          )}

          {purchasesError && (
            <Typography variant="body2" color="error" sx={{ mt: 1, ...blurredTextSx }}>
              {purchasesError}
            </Typography>
          )}

          {purchaseActionMessage && (
            <Box
              sx={{
                mt: 1.5,
                mb: 1,
                px: 1.5,
                py: 1,
                borderRadius: 2,
                backgroundColor: purchaseActionMessage.toLowerCase().includes("unable")
                  ? "#fee2e2"
                  : "#ecfdf5",
                color: purchaseActionMessage.toLowerCase().includes("unable")
                  ? "#991b1b"
                  : "#065f46",
              }}
            >
              <Typography variant="body2">{purchaseActionMessage}</Typography>
            </Box>
          )}

          {!purchasesLoading && !purchasesError && purchases.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ...blurredTextSx }}>
              No purchases yet.
            </Typography>
          )}

          {!purchasesLoading && !purchasesError && purchases.length > 0 && (
            <Box sx={{ position: "relative", mt: 1 }}>
              {!isSm && (
                <>
                  <IconButton
                    aria-label="Previous purchases"
                    onClick={() => scrollPurchases(-1)}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: -8,
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      bgcolor: "rgba(255,255,255,0.9)",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                    size="small"
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  <IconButton
                    aria-label="Next purchases"
                    onClick={() => scrollPurchases(1)}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      right: -8,
                      transform: "translateY(-50%)",
                      zIndex: 2,
                      bgcolor: "rgba(255,255,255,0.9)",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    }}
                    size="small"
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </>
              )}

              <Box
                ref={purchasesTrackRef}
                sx={{
                  display: "flex",
                  gap: 2,
                  overflowX: "auto",
                  scrollSnapType: "x mandatory",
                  scrollBehavior: "smooth",
                  WebkitOverflowScrolling: "touch",
                  px: 1,
                  py: 0.5,
                  "&::-webkit-scrollbar": { height: 8 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#d1d5db",
                    borderRadius: 8,
                  },
                  "& > *": {
                    scrollSnapAlign: "start",
                    flex: "0 0 auto",
                  },
                }}
              >
                {purchases.map((purchase) => {
                  const art = purchase.artwork || {};
                  const artId = art.id || purchase.artwork_id;
                  const purchaseDate = purchase.sale_date
                    ? new Date(purchase.sale_date).toLocaleDateString()
                    : "—";
                  const purchasePrice =
                    purchase.price !== null && purchase.price !== undefined
                      ? `$${purchase.price}`
                      : "—";

                  return (
                    <Box
                      key={purchase.sale_id || purchase.id || artId}
                      sx={{
                        width: PURCHASE_CARD_WIDTH,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        borderRadius: 2,
                        boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <MiniArtworkCard
                        id={artId}
                        artwork={art}
                        width={PURCHASE_CARD_WIDTH}
                        imgHeight={160}
                        onClick={() => openPurchaseModal(artId, art)}
                      />

                      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                        <Typography variant="body2" color="text.primary" fontWeight={600}>
                          Purchase details
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Date:</strong> {purchaseDate}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Price:</strong> {purchasePrice}
                        </Typography>
                        {purchase.payment?.status && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Status:</strong> {purchase.payment.status}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap" }}>
                          {purchase.payment?.receipt_url && (
                            <Button
                              size="small"
                              variant="outlined"
                              href={purchase.payment.receipt_url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View receipt
                            </Button>
                          )}
                          {artId && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => openPurchaseModal(artId, art)}
                            >
                              View artwork
                            </Button>
                          )}
                          {purchase.payment?.refundable && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              disabled={refundingSaleId === purchase.sale_id}
                              onClick={() => handleRequestRefund(purchase)}
                            >
                              {refundingSaleId === purchase.sale_id ? "Requesting refund..." : "Request refund"}
                            </Button>
                          )}
                        </Stack>
                        {purchase.payment?.status === "refund_pending" && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            Stripe is processing this refund request. We&apos;ll update the payment once it is confirmed.
                          </Typography>
                        )}
                        {purchase.payment?.status === "refund_requested" && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                            This refund request is waiting for seller approval before Stripe is asked to process it.
                            {purchase.payment?.refund_request_reason ? ` Reason: ${purchase.payment.refund_request_reason}` : ""}
                          </Typography>
                        )}
                        {purchase.payment?.status === "refund_rejected" && (
                          <Typography variant="caption" sx={{ mt: 0.5, color: "#b91c1c" }}>
                            This refund request was rejected by the seller.
                            {purchase.payment?.refund_rejection_reason ? ` Reason: ${purchase.payment.refund_rejection_reason}` : ""}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog open={!!selectedPurchaseId} onClose={closePurchaseModal} maxWidth="md" fullWidth>
        <Box sx={{ p: 2, bgcolor: "#f9fafb" }}>
          {selectedPurchaseLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : selectedPurchaseArtwork ? (
            <ArtworkCard artwork={selectedPurchaseArtwork} showActions={false} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Failed to load artwork details.
            </Typography>
          )}
        </Box>
      </Dialog>

      {/* My Favorites Tab */}
      {tab === 3 && (
        <Box sx={{ mt: 2, gap: 2, display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
          <FavoritesSection type="artworks" />
          <FavoritesSection type="artists" />
          <FavoritesSection type="hosts" />
        </Box>
      )}

      <ArtworkSearchFiltersModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        statuses={ARTWORK_STATUSES}
        statusFilter={statusFilterNearby}
        onStatusChange={setStatusFilterNearby}
        selectedCategory={selectedCategory}
        onCategoryChange={(value) => {
          setSelectedCategory(value);
          setSelectedSubcategories({});
        }}
        selectedSubcategories={selectedSubcategories}
        onSubcategoryChange={(sub, value) => {
          setSelectedSubcategories((prev) => ({ ...prev, [sub]: value }));
        }}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onMinPriceChange={setMinPrice}
        onMaxPriceChange={setMaxPrice}
        onClearAll={() => {
          setStatusFilterNearby([]);
          setSelectedCategory('');
          setSelectedSubcategories({});
          setMinPrice('');
          setMaxPrice('');
        }}
      />

      <ChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        conversation={conversation}
        messages={messages}
        onSend={handleSend}
        newMsg={newMsg}
        setNewMsg={setNewMsg}
        authUserId={authUserId}
        messagesEndRef={messagesEndRef}
        recipient={recipient}
      />
    </Container>
  );
}
